// eslint-disable-next-line import/no-unresolved
import plyr from 'plyr';

const getNearestPreviousHeadlineText = ($element) => {
  if (!$element) {
    return '';
  }

  const headlines = Array.from(
    document.querySelectorAll(
      'h1, h2, h3, h4, h5, h6, [role="heading"], .headline',
    ),
  );
  let headlineText = '';

  headlines.forEach(($headline) => {
    if ($headline === $element || $headline.contains($element)) {
      return;
    }

    const relation = $headline.compareDocumentPosition($element);
    if (relation === Node.DOCUMENT_POSITION_FOLLOWING) {
      const text = $headline.textContent && $headline.textContent.trim();
      if (text) {
        headlineText = text;
      }
    }
  });

  return headlineText;
};

const getPlayerLabel = (player, playerIndex) => {
  const original = player && player.elements && player.elements.original;
  const container = player && player.elements && player.elements.container;
  const explicitLabel =
    original &&
    (original.getAttribute('aria-label') || original.getAttribute('title'));

  if (explicitLabel) {
    return explicitLabel;
  }

  const previousHeadlineText = getNearestPreviousHeadlineText(container);
  if (previousHeadlineText) {
    return `Videoplayer: ${previousHeadlineText}`;
  }

  const $caption =
    container &&
    container.closest('figure') &&
    container.closest('figure').querySelector('figcaption');

  if ($caption && $caption.textContent && $caption.textContent.trim()) {
    return `Videoplayer: ${$caption.textContent.trim()}`;
  }

  return `Videoplayer ${playerIndex + 1}`;
};

const sanitizePlyrA11y = (player, playerLabel) => {
  const container = player && player.elements && player.elements.container;

  if (!container) {
    return;
  }

  if (container.getAttribute('role') !== 'application') {
    container.setAttribute('role', 'application');
  }

  if (container.getAttribute('aria-label') !== playerLabel) {
    container.setAttribute('aria-label', playerLabel);
  }

  container.querySelectorAll('input[type="range"]').forEach(($input) => {
    $input.removeAttribute('role');
    $input.removeAttribute('aria-valuemin');
    $input.removeAttribute('aria-valuemax');
    $input.removeAttribute('aria-valuenow');
  });

  container.querySelectorAll('.plyr__time[aria-label]').forEach(($time) => {
    if ($time.getAttribute('role') !== 'timer') {
      $time.setAttribute('role', 'timer');
    }
  });
};

const observePlyrMutations = (player, playerLabel) => {
  const container = player && player.elements && player.elements.container;

  if (!container || typeof MutationObserver === 'undefined') {
    return;
  }

  let sanitizeScheduled = false;
  const scheduleSanitize = () => {
    if (sanitizeScheduled) {
      return;
    }

    sanitizeScheduled = true;
    window.requestAnimationFrame(() => {
      sanitizeScheduled = false;
      sanitizePlyrA11y(player, playerLabel);
    });
  };

  const observer = new MutationObserver((mutations) => {
    const shouldSanitize = mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        return true;
      }

      if (mutation.type === 'childList') {
        return (
          mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0
        );
      }

      return false;
    });

    if (shouldSanitize) {
      scheduleSanitize();
    }
  });

  observer.observe(container, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'role',
      'aria-valuemin',
      'aria-valuemax',
      'aria-valuenow',
      'aria-label',
    ],
  });

  player.on('destroyed', () => {
    observer.disconnect();
  });
};

const players = plyr.setup(document.querySelectorAll('.figure__video'), {
  loadSprite: false,
  i18n: {
    restart: 'Neustarten',
    rewind: '{seektime} Sekunden zurückspulen',
    play: 'Abspielen',
    pause: 'Pausieren',
    forward: '{seektime} Sekunden vorspulen',
    buffered: 'vorgeladen',
    currentTime: 'Aktuelle Abspielzeit',
    duration: 'Länge',
    volume: 'Lautstärke',
    toggleMute: 'Ton an-/abschalten',
    toggleCaptions: 'Untertitel an-/abschalten',
    toggleFullscreen: 'Vollbild an-/abschalten',
  },
  iconPrefix: 'icon',
  controls: [
    'play-large', // The large play button in the center
    'play', // Play/pause playback
    'progress', // The progress bar and scrubber for playback and buffering
    'current-time', // The current time of playback
    'duration', // The full duration of the media
    'mute', // Toggle mute
    'volume', // Volume control
    'captions', // Toggle captions
    'fullscreen', // Toggle fullscreen
  ],
});

players?.forEach((player, playerIndex) => {
  const playerLabel = getPlayerLabel(player, playerIndex);

  player.on('ready', () => {
    sanitizePlyrA11y(player, playerLabel);
    observePlyrMutations(player, playerLabel);
  });
});
